/**
 * データ整合 ＞「Notionデータ整合」タブ — 2026-07-28 にタブ化（機能は 2026-07-12 版のまま）
 *
 * 1) sys と Notion 車両マスタの「存在の差分」を突き合わせて表示
 *    （sysにあってNotionにない / Notionにあってsysにない → 足りないデータを埋める）
 * 2) Notion 事故・修理DBで状態が「完了」でない車両を表示
 * 3) 下部のコンテキストウィンドウ（Gemini対話）で抽出・補完を行う
 * API: sys/reconcile/api.php（overview / repairs / chat）
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Checkbox, FormControlLabel, Chip, IconButton, TextField, CircularProgress, Link, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BuildIcon from '@mui/icons-material/Build';
import { LINE_SYNC_KEY } from '../../config/instances';

const RECONCILE_API_URL = (process.env.REACT_APP_RECONCILE_API_URL as string)
    || 'https://booking.trailernchu.co.jp/sys/reconcile/api.php';
const API_KEY = LINE_SYNC_KEY; // agent API と共通の内部キー

interface Row {
    key: string;
    label: string;
    car_type: string;
    sys_status: string;
    sys_archived: boolean;
    in_sys: boolean;
    in_notion: boolean;
    notion_url: string;
}

interface Repair {
    vehicle: string;
    report: string;
    status: string;
    url: string;
}

interface ChatMsg {
    role: 'user' | 'assistant';
    text: string;
    badges?: string[];
}

const TOOL_BADGE: Record<string, string> = {
    list_missing: 'geminiで処理',
    create_notion_vehicle: 'notionにページ作成',
    list_active_repairs: 'notionに問い合わせ',
};

const callApi = async (action: string, body: any): Promise<any> => {
    const res = await fetch(`${RECONCILE_API_URL}?action=${action}&api_key=${API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'APIエラー');
    return data;
};

const NotionTab: React.FC = () => {
    const [rows, setRows] = useState<Row[]>([]);
    const [counts, setCounts] = useState<{ total: number; both: number; sys_only: number; notion_only: number } | null>(null);
    const [repairs, setRepairs] = useState<Repair[]>([]);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [diffOnly, setDiffOnly] = useState(true);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const fetchAll = useCallback(async (archived: boolean) => {
        setLoading(true);
        try {
            const [ov, rp] = await Promise.all([
                callApi('overview', { include_archived: archived }),
                callApi('repairs', {}),
            ]);
            setRows(ov.rows || []);
            setCounts(ov.counts || null);
            setRepairs(rp.reports || []);
        } catch (e: any) {
            setMessages((prev) => [...prev, { role: 'assistant', text: `一覧の取得に失敗しました: ${e.message}` }]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(includeArchived); }, [includeArchived, fetchAll]);
    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

    const send = async () => {
        const text = input.trim();
        if (!text || sending) return;
        setInput('');
        const nextMessages: ChatMsg[] = [...messages, { role: 'user', text }];
        setMessages(nextMessages);
        setSending(true);
        try {
            const data = await callApi('chat', {
                messages: nextMessages.map((m) => ({ role: m.role, text: m.text })),
                include_archived: includeArchived,
            });
            const badges: string[] = Array.from(new Set(
                (data.events || []).map((ev: any) => TOOL_BADGE[ev.tool] || ev.tool)
            ));
            setMessages((prev) => [...prev, { role: 'assistant', text: data.reply, badges }]);
            if (data.updated) fetchAll(includeArchived); // Notion にページ作成があったら再取得
        } catch (e: any) {
            setMessages((prev) => [...prev, { role: 'assistant', text: `エラー: ${e.message}` }]);
        } finally {
            setSending(false);
        }
    };

    const visibleRows = diffOnly ? rows.filter((r) => !(r.in_sys && r.in_notion)) : rows;

    const presenceChip = (r: Row) => {
        if (r.in_sys && r.in_notion) return <Chip size="small" color="success" label="両方" sx={{ height: 20 }} />;
        if (r.in_sys) return <Chip size="small" color="warning" label="Notionに無い" sx={{ height: 20 }} />;
        return <Chip size="small" color="info" label="sysに無い" sx={{ height: 20 }} />;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
            {/* ツールバー */}
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                    sys と Notion の存在差分を突き合わせ、足りないデータを対話で埋めます
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {counts && (
                        <>
                            <Chip size="small" label={`全${counts.total}台`} />
                            <Chip size="small" color="success" label={`両方 ${counts.both}`} />
                            <Chip size="small" color="warning" label={`Notionに無い ${counts.sys_only}`} />
                            <Chip size="small" color="info" label={`sysに無い ${counts.notion_only}`} />
                        </>
                    )}
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
                        label={<Typography variant="body2">アーカイブも含める</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={diffOnly} onChange={(e) => setDiffOnly(e.target.checked)} />}
                        label={<Typography variant="body2">差分のみ</Typography>}
                    />
                    <IconButton onClick={() => fetchAll(includeArchived)} disabled={loading} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* 上段: 存在差分 + 修理未完了 */}
            <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, minHeight: 0 }}>
                {/* 存在差分テーブル */}
                <TableContainer component={Paper} elevation={1} sx={{ flex: 2, minWidth: 0, borderRadius: 3 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
                    ) : (
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>番号</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>車種</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>sys 状態</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>存在</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows.map((r, i) => (
                                    <TableRow key={`${r.key}-${i}`} hover sx={{ bgcolor: r.in_sys && r.in_notion ? undefined : (r.in_sys ? '#fff7ed' : '#eff6ff') }}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {r.label}
                                            {r.sys_archived && <Chip size="small" label="ARC" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
                                        </TableCell>
                                        <TableCell>{r.car_type || '—'}</TableCell>
                                        <TableCell>{r.sys_status || '—'}</TableCell>
                                        <TableCell>{presenceChip(r)}</TableCell>
                                        <TableCell>
                                            {r.notion_url && (
                                                <Link href={r.notion_url} target="_blank" rel="noopener noreferrer">
                                                    <OpenInNewIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                </Link>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>

                {/* 修理未完了テーブル */}
                <TableContainer component={Paper} elevation={1} sx={{ flex: 1, minWidth: 260, borderRadius: 3 }}>
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BuildIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            修理が完了していない車両（{repairs.length}件）
                        </Typography>
                    </Box>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>車両</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>報告</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>状態</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {repairs.map((rp, i) => (
                                <TableRow key={i} hover>
                                    <TableCell sx={{ fontWeight: 600 }}>{rp.vehicle}</TableCell>
                                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rp.report}</TableCell>
                                    <TableCell><Chip size="small" label={rp.status || '—'} sx={{ height: 20 }} /></TableCell>
                                    <TableCell>
                                        {rp.url && (
                                            <Link href={rp.url} target="_blank" rel="noopener noreferrer">
                                                <OpenInNewIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                            </Link>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* コンテキストウィンドウ（対話） */}
            <Paper elevation={2} sx={{ mt: 2, borderRadius: 3, p: 2, display: 'flex', flexDirection: 'column', maxHeight: '34vh' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
                    コンテキストウィンドウ（例: 「sysにあってnotionにない車両は？」「その車両をnotionに登録して」「修理が完了していない車両は？」）
                </Typography>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 80, mb: 1 }}>
                    {messages.map((m, i) => (
                        <Box key={i} sx={{ mb: 1, textAlign: m.role === 'user' ? 'right' : 'left' }}>
                            {m.role === 'assistant' && (m.badges || []).map((b) => (
                                <Chip key={b} size="small" label={b} sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: 11, bgcolor: '#e0f2fe' }} />
                            ))}
                            <Paper elevation={0} sx={{
                                display: 'inline-block', px: 1.5, py: 1, borderRadius: 2, maxWidth: '85%',
                                bgcolor: m.role === 'user' ? '#0ea5e9' : '#f1f5f9',
                                color: m.role === 'user' ? '#fff' : '#1e293b',
                                whiteSpace: 'pre-wrap', textAlign: 'left', fontSize: 14,
                            }}>
                                {m.text}
                            </Paper>
                        </Box>
                    ))}
                    {sending && <CircularProgress size={18} sx={{ ml: 1 }} />}
                    <div ref={chatEndRef} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth size="small" placeholder="整合の指示や質問を入力（Enterで送信）"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                        disabled={sending}
                    />
                    <Button variant="contained" onClick={send} disabled={sending || !input.trim()} endIcon={<SendIcon />}>
                        送信
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default NotionTab;
